// REST client for the merchant application (backend routes/merchantOnboarding.js).
// The applicant is identified by the link token from their email, kept in
// localStorage on this device so a refresh doesn't lose their place.

const TOKEN_KEY = "rmf-merchant-application";

export function serverUrl() {
  return (process.env.REACT_APP_SERVER_URL || "http://localhost:8001/").replace(/\/+$/, "");
}

export const tokenStore = {
  get() {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  },
  set(token) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      // private mode: the emailed link still works
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      // nothing stored
    }
  },
};

// A super admin or an agent filling in an application (opened from the admin
// panel). Kept for this tab only, apart from the merchant's own link, so it
// never lands on a shared computer's saved application.
const STAFF_KEY = "rmf-staff-application";
const STAFF_START_KEY = "rmf-staff-start";
function session(key) {
  return {
    get() {
      try { return sessionStorage.getItem(key); } catch (e) { return null; }
    },
    set(v) {
      try { sessionStorage.setItem(key, v); } catch (e) { /* private mode: this tab keeps it in memory */ }
    },
    clear() {
      try { sessionStorage.removeItem(key); } catch (e) { /* nothing stored */ }
    },
  };
}
export const staffStore = session(STAFF_KEY);
export const staffStartStore = session(STAFF_START_KEY);

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.status = status;
    this.details = details || {};
  }
}

// In the admin's frame (PartnerSignup sets this): a staff link that has
// expired (12 hours) is renewed by the admin page, and the request is sent
// once more with the new token, so nothing typed is lost.
let staffTokenRenewer = null;
export function setStaffTokenRenewer(fn) {
  staffTokenRenewer = fn;
}

// uploadToken: the phone's documents-only token from the QR code.
export async function api(method, path, { token, uploadToken, body, form, retried } = {}) {
  const headers = {};
  if (token) headers["x-onboarding-token"] = token;
  if (uploadToken) headers["x-upload-token"] = uploadToken;
  let payload;
  if (form) payload = form;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(`${serverUrl()}/merchant-onboarding${path}`, { method, headers, body: payload });
  } catch (e) {
    throw new ApiError("We couldn't reach RushMyFood. Check your connection and try again.", 0);
  }
  const type = res.headers.get("content-type") || "";
  if (res.ok && type.includes("application/pdf")) return res.blob();
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !retried && staffTokenRenewer && String(token || "").includes(".")) {
      const fresh = await staffTokenRenewer(token).catch(() => null);
      if (fresh && fresh !== token) return api(method, path, { token: fresh, uploadToken, body, form, retried: true });
    }
    throw new ApiError(data.error || "Something went wrong. Please try again.", res.status, data.details);
  }
  return data;
}

function saveBlob(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Fetch a PDF with the token and hand it to the browser as a download. */
export async function downloadPdf(path, token, filename) {
  const blob = await api("GET", path, { token });
  const url = URL.createObjectURL(blob);
  saveBlob(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Fetch a PDF with the token and open it in a new tab; a download if the tab was blocked. */
export async function openPdf(path, token, filename) {
  // Opened before the fetch, while it still counts as the user's click.
  const tab = window.open("", "_blank");
  try {
    const blob = await api("GET", path, { token });
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else saveBlob(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}
